import React, { useMemo, useState, useCallback, useEffect } from "react";
import ReactFlow, { Background, Controls, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import {
  Plane, Mail, Scale, FileText, FolderOpen, Building2,
  Landmark, Banknote, Anchor as AnchorIcon, FileSearch,
  Phone, CalendarClock, MapPin, Image as ImageIcon, User,
} from "lucide-react";
import { KIND_META, KIND_CHIP, ICON_CARD_KINDS, REL_META, CONFIDENCE_META, deriveLegalLabel, kindMetaBadges } from "./efConsts";
import { trackAudienceEvent } from "../LiveAudienceTracker";

// Map entity.kind → big amber icon component used for icon-card rendering.
// Matches the reference case-board screenshot (envelope = comms, scales =
// court, plane = flight, folder = documents/flight logs).
const KIND_ICON_COMPONENT = {
  aircraft: Plane, flight: Plane, flight_event: Plane, vessel: AnchorIcon,
  email: Mail, communications: Mail,
  document: FolderOpen, photo: ImageIcon, evidence: ImageIcon,
  filing: Scale, legal_record: Scale,
  financial_entity: Banknote, shell_company: Banknote, trust: Banknote, account: Banknote, financial: Banknote,
  government_office: Landmark, government_record: FileText,
  organization: Building2, company: Building2,
  property: Building2, location: MapPin,
  phone_number: Phone, event: CalendarClock, timeline_event: CalendarClock,
  person: User, reference: FileSearch, alias: FileSearch,
};

/**
 * Custom node renderer — polished dossier card aesthetic.
 * Tier 0 (anchor) renders larger with red glow; tier 1 (close associates) and
 * tier 2 (institutions / orgs / property / docs) render at the same secondary scale.
 */
const EvidenceNode = ({ data }) => {
  const { entity, isAnchor, onSelect, anchorImage, tier, isSelected, isDimmed } = data;
  const meta = KIND_META[entity.kind] || KIND_META.reference;
  const conf = CONFIDENCE_META[entity.confidence_level] || null;
  const img = isAnchor
    ? (anchorImage || entity.thumbnail_url || entity.photo_url || entity.image_url)
    : (entity.thumbnail_url || entity.photo_url || entity.image_url);
  const tagline = entity.occupation_or_role || entity.connection_summary_to_epstein || "";

  // Tier-driven sizing keeps the board feeling premium and intentional.
  // Anchor is deliberately oversized so Jeffrey Epstein visually dominates regardless
  // of which peripheral node the viewer has selected.
  const width = isAnchor ? 280 : tier === 1 ? 184 : 168;

  // Card styling: red ring + glow is RESERVED for the anchor. Selected non-anchor
  // cards get a warm amber outline so the eye can still find them, but never
  // compete with Jeffrey Epstein's red-string visual authority.
  let cardClass;
  if (isAnchor) {
    cardClass =
      "border-[#D92D20] shadow-[0_0_0_3px_rgba(217,45,32,0.35),0_0_0_8px_rgba(217,45,32,0.10),0_16px_44px_rgba(0,0,0,0.7),0_0_90px_rgba(217,45,32,0.12)]";
  } else if (isSelected) {
    cardClass =
      // Strong amber ring + dark shadow so the active card always reads above
      // surrounding edges and labels.
      "border-[#E0A526] shadow-[0_0_0_3px_rgba(224,165,38,0.55),0_0_0_10px_rgba(224,165,38,0.18),0_16px_44px_rgba(0,0,0,0.75)]";
  } else if (tier === 1) {
    cardClass = "border-[#5a544a] shadow-[0_6px_18px_rgba(0,0,0,0.55)] hover:border-[#E0A526]/70";
  } else {
    cardClass = "border-[#3A352E] shadow-[0_4px_14px_rgba(0,0,0,0.5)] hover:border-[#E0A526]/70";
  }

  // Dim non-selected nodes when a selection exists. The selected node itself
  // (and the anchor) stays full opacity and is rendered above everything.
  const dimStyle = isDimmed ? { opacity: 0.32, filter: "saturate(0.6)" } : null;
  const liftStyle = isSelected
    ? { transform: "translateY(-2px) scale(1.03)", transformOrigin: "center" }
    : null;

  return (
    <div
      onClick={(ev) => {
        ev.stopPropagation();
        try {
          trackAudienceEvent("node_click", {
            label: entity?.name || entity?.id,
            meta: entity?.kind || "node",
          });
        } catch (_) { /* silent */ }
        onSelect?.(entity);
      }}
      data-testid={`ef-node-${entity.id}`}
      data-active={isSelected ? "true" : "false"}
      className={`group relative cursor-pointer select-none border bg-[#0a0907] text-[#E4DFD3] transition-all duration-200 hover:-translate-y-0.5 ${cardClass}`}
      style={{ width, ...(dimStyle || {}), ...(liftStyle || {}) }}
    >
      <Handle type="target" position={Position.Top} className="!bg-[#D92D20] !w-1.5 !h-1.5 !border-0 !opacity-0" />

      {/* Anchor-only header strip — keeps Jeffrey Epstein visually unmistakable */}
      {isAnchor && (
        <div className="bg-[#D92D20] text-white font-mono text-[9px] uppercase tracking-[0.35em] px-3 py-1.5 flex items-center justify-between">
          <span>// ANCHOR SUBJECT</span>
          <span className="opacity-90">CASE 01</span>
        </div>
      )}

      {/* Kind chip floats in TOP-RIGHT corner per reference screenshot
          (PERSON / ORG / DOC / PROPERTY / FLIGHT / COMMS / COURT / EVIDENCE) */}
      {!isAnchor && (
        <div
          className="absolute top-1.5 right-1.5 z-10 font-mono text-[8px] uppercase tracking-[0.3em] px-1.5 py-0.5 bg-[#0a0907]/95 border"
          style={{ color: meta.color, borderColor: `${meta.color}80` }}
          data-testid={`ef-node-chip-${entity.id}`}
        >
          {KIND_CHIP[entity.kind] || meta.label}
        </div>
      )}

      {/* Photo / portrait OR large amber icon-card body */}
      <div className="bg-[#0a0907] aspect-[4/5] flex items-center justify-center border-b border-[#2D2A26] relative overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={entity.name}
            loading="lazy"
            decoding="async"
            // For non-anchor portrait cards, bias the crop to the top so
            // small/passport-style headshots (where the face sits in the upper
            // third) don't show dead space below. The anchor uses a curated
            // full-bleed image and keeps default center cropping.
            className={
              "w-full h-full object-cover filter grayscale-[18%] contrast-105 " +
              (isAnchor ? "object-center" : "object-top")
            }
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : ICON_CARD_KINDS.has(entity.kind) ? (
          // Icon-card layout — matches the reference screenshot's plane / envelope /
          // scales / folder cards. Large centered amber icon, no "no photo on file"
          // text. This is the visual identity for non-photo evidence nodes.
          (() => {
            const IconComp = KIND_ICON_COMPONENT[entity.kind] || FileText;
            return (
              <div className="flex items-center justify-center w-full h-full" data-testid={`ef-icon-card-${entity.id}`}>
                <IconComp
                  size={isAnchor ? 96 : 64}
                  strokeWidth={1.4}
                  style={{ color: meta.color, filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.65))" }}
                />
              </div>
            );
          })()
        ) : (
          <div className="flex flex-col items-center justify-center text-[#454038]" data-testid={`ef-silhouette-${entity.id}`}>
            <div className={isAnchor ? "text-6xl" : "text-3xl"} style={{ color: isAnchor ? "#D92D20" : meta.color }}>{meta.icon}</div>
            <div className="font-mono text-[7px] uppercase tracking-[0.3em] mt-1.5 text-[#5a544a]">
              no photo on file
            </div>
          </div>
        )}
        {/* Subtle gradient + faint grain for premium feel */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-black/50" />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "3px 3px" }}
        />
        {/* Anchor-only crimson wash */}
        {isAnchor && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(217,45,32,0.10) 0%, transparent 60%)" }}
          />
        )}
        {/* Confidence badge — moved to bottom-right so it never collides with
            the new top-right kind chip. */}
        {conf && (
          <div
            className="absolute bottom-1.5 right-1.5 font-mono text-[7px] uppercase tracking-[0.25em] px-1.5 py-0.5 backdrop-blur-sm bg-black/75"
            style={{ color: conf.color, border: `1px solid ${conf.color}80` }}
          >
            {conf.label}
          </div>
        )}
        {/* File-tag corner accent for non-anchor cards */}
        {!isAnchor && (
          <div className="absolute top-0 left-0 w-0 h-0 border-l-[10px] border-l-[#D92D20]/60 border-b-[10px] border-b-transparent" />
        )}
      </div>

      {/* Caption */}
      <div className={`${isAnchor ? "px-3 py-2.5" : "px-2.5 py-2"} bg-[#0a0907]`}>
        {/* Anchor still gets the inline PERSON label in caption since its
            distinctive red header is the dominant identifier; for non-anchor
            cards the kind chip already lives in the top-right corner so we
            skip the redundant inline label. */}
        {isAnchor && (
          <div className="font-mono text-[8px] uppercase tracking-[0.3em] truncate mb-0.5" style={{ color: meta.color }}>
            {meta.label}
          </div>
        )}
        <div
          className={`font-heading ${isAnchor ? "text-lg" : "text-[13px]"} leading-tight text-white truncate`}
          title={entity.name}
        >
          {entity.name}
        </div>
        {tagline && (
          <div className="text-[10px] text-[#A39E93] leading-snug mt-1 line-clamp-3" title={tagline}>
            {tagline}
          </div>
        )}
        {!isAnchor && (() => {
          const badges = kindMetaBadges(entity);
          if (!badges.length) return null;
          return (
            <div
              className="mt-1.5 pt-1.5 border-t border-[#2D2A26] grid gap-x-1.5 gap-y-0.5"
              style={{ gridTemplateColumns: "min-content 1fr" }}
              data-testid={`ef-node-meta-${entity.id}`}
            >
              {badges.map((b) => (
                <React.Fragment key={b.label}>
                  <span
                    className="font-mono text-[7px] uppercase tracking-[0.25em] text-[#757065] whitespace-nowrap leading-snug"
                  >
                    {b.label}
                  </span>
                  <span
                    className="font-mono text-[8.5px] text-[#CBB892] truncate leading-snug"
                    title={b.value}
                  >
                    {b.value}
                  </span>
                </React.Fragment>
              ))}
            </div>
          );
        })()}
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-[#D92D20] !w-1.5 !h-1.5 !border-0 !opacity-0" />
    </div>
  );
};

const nodeTypes = { evidence: EvidenceNode };

/**
 * Tiered / orbital layout — Palantir-style organized investigation board.
 *
 *   Tier 0 (anchor)      → dead center, larger
 *   Tier 1 (close ring)  → person-kind entities, evenly spaced on the inner ring
 *   Tier 2 (outer ring)  → organizations, institutions, properties, documents,
 *                          government offices, locations, etc.
 *
 * Card dimensions are accounted for so cards never overlap and the rings stay
 * visually clean even when one tier is sparse. Returns absolute (x,y) of each
 * card's top-left corner — ReactFlow positions cards from top-left.
 */
const TIER1_KINDS = new Set(["person"]);
const CARD_W = { 0: 280, 1: 184, 2: 168 };
const CARD_H = { 0: 370, 1: 254, 2: 244 };

function classifyTier(entity, anchorId) {
  if (entity.id === anchorId) return 0;
  if (TIER1_KINDS.has(entity.kind)) return 1;
  return 2;
}

function tieredOrbitalLayout(nodes, anchorId) {
  // Group nodes by tier
  const tier1 = [];
  const tier2 = [];
  let anchor = null;
  nodes.forEach((n) => {
    const t = classifyTier(n, anchorId);
    if (t === 0) anchor = n;
    else if (t === 1) tier1.push(n);
    else tier2.push(n);
  });

  // Order tier 2 by category for predictability — properties/locations together,
  // institutions/orgs together, finance together, etc.
  const tier2Order = {
    location: 1, custody_facility: 2,
    property: 1,
    organization: 3, institution: 3, government_office: 3,
    company: 5,
    reference: 6, document: 7, other: 9,
  };
  tier2.sort((a, b) => (tier2Order[a.kind] || 9) - (tier2Order[b.kind] || 9));
  tier1.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // ---- Radii ----
  // Tier-1 radius must clear the anchor card's outer edge first, then satisfy
  // its own neighbor-spacing requirement.
  const t1Clear = CARD_H[0] / 2 + 60 + CARD_H[1] / 2; // anchor half-height + gap + tier1 half-height
  const t1MinPerimeter = (CARD_W[1] + 80) * Math.max(tier1.length, 1);
  const t1NeighborRadius = t1MinPerimeter / (2 * Math.PI);
  const t1Radius = Math.max(t1NeighborRadius, t1Clear, 280);

  // Tier-2 radius needs to clear tier-1's outer edge AND avoid neighbor overlap.
  const t2MinPerimeter = (CARD_W[2] + 90) * Math.max(tier2.length, 1);
  const t2NeighborRadius = t2MinPerimeter / (2 * Math.PI);
  const t2ClearRadius =
    t1Radius + CARD_H[1] / 2 + 80 + CARD_H[2] / 2;
  const t2Radius = Math.max(t2NeighborRadius, t2ClearRadius);

  // Canvas big enough to comfortably hold the outer ring + card overhang.
  const width = (t2Radius + CARD_W[2]) * 2 + 120;
  const height = (t2Radius + CARD_H[2]) * 2 + 120;
  const cx = width / 2;
  const cy = height / 2;

  const out = {};

  if (anchor) {
    out[anchor.id] = { x: cx - CARD_W[0] / 2, y: cy - CARD_H[0] / 2 };
  }

  const t1Count = tier1.length;
  tier1.forEach((n, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / Math.max(t1Count, 1);
    out[n.id] = {
      x: cx + Math.cos(a) * t1Radius - CARD_W[1] / 2,
      y: cy + Math.sin(a) * t1Radius - CARD_H[1] / 2,
    };
  });

  const t2Count = tier2.length;
  const t2Offset = -Math.PI / 2 + Math.PI / Math.max(t2Count, 1);
  tier2.forEach((n, i) => {
    const a = t2Offset + (i * 2 * Math.PI) / Math.max(t2Count, 1);
    out[n.id] = {
      x: cx + Math.cos(a) * t2Radius - CARD_W[2] / 2,
      y: cy + Math.sin(a) * t2Radius - CARD_H[2] / 2,
    };
  });

  nodes.forEach((n) => { if (!out[n.id]) out[n.id] = { x: 0, y: 0 }; });
  return out;
}

/** Compact edge label: prefer date_label, then "N evidence" / "N src". Empty if nothing useful. */
function compactEdgeLabel(r) {
  if (r.date_label) return r.date_label;
  const evCount = (r.evidence_ids || []).length;
  if (evCount > 0) return `${evCount} evidence`;
  const srcCount = (r.source_refs || []).length;
  if (srcCount > 1) return `${srcCount} src`;
  return "";
}

/** Floating hover tooltip — compact dossier card for an edge. */
const EdgeTooltip = ({ rel, x, y, nodeMap }) => {
  if (!rel) return null;
  const meta = REL_META[rel.type] || REL_META.other_editor_defined;
  const conf = CONFIDENCE_META[rel.confidence] || CONFIDENCE_META.medium;
  const legal = deriveLegalLabel(rel);
  const src = nodeMap[rel.src_entity_id];
  const dst = nodeMap[rel.dst_entity_id];
  const evCount = (rel.evidence_ids || []).length;
  const srcCount = (rel.source_refs || []).length;
  return (
    <div
      className="absolute z-30 pointer-events-none border border-[#D92D20]/60 bg-[#0F0F0F] shadow-[0_8px_24px_rgba(0,0,0,0.6)] px-3 py-2 w-[260px]"
      style={{ left: Math.min(x + 12, 9999), top: Math.max(y - 12, 0) }}
      data-testid="ef-edge-tooltip"
    >
      <div className="flex flex-wrap items-center gap-1 mb-1.5">
        <span style={{ color: meta.color, borderColor: meta.color }}
          className="font-mono text-[8px] uppercase tracking-[0.25em] border px-1.5 py-0.5">
          {meta.label}
        </span>
        {legal && (
          <span style={{ color: legal.color, borderColor: legal.color }}
            className="font-mono text-[8px] uppercase tracking-[0.25em] border px-1.5 py-0.5">
            {legal.label}
          </span>
        )}
      </div>
      {(src || dst) && (
        <div className="font-heading text-[12px] text-[#E4DFD3] leading-tight mb-1.5">
          {src?.name || "?"} <span className="text-[#D92D20]">↔</span> {dst?.name || "?"}
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 font-mono text-[9px]">
        <div className="text-[#757065]">date</div>
        <div className="text-[#E4DFD3] truncate">{rel.date_label || "—"}</div>
        <div className="text-[#757065]">evidence</div>
        <div className="text-[#E4DFD3]">{evCount}</div>
        <div className="text-[#757065]">sources</div>
        <div className="text-[#E4DFD3]">{srcCount}</div>
        <div className="text-[#757065]">confidence</div>
        <div style={{ color: conf.color }}>{conf.label}</div>
      </div>
    </div>
  );
};

export const EvidenceBoard = ({
  nodes = [], edges = [], anchorId, anchorImage = null,
  onSelectEntity, onSelectEdge, onClearSelection,
  selectedEntityId = null, height = 680,
}) => {
  const [hover, setHover] = useState(null); // { rel, x, y }
  const [selectedRelId, setSelectedRelId] = useState(null);

  const nodeMap = useMemo(
    () => Object.fromEntries(nodes.map((n) => [n.id, n])),
    [nodes],
  );

  const { flowNodes, flowEdges } = useMemo(() => {
    const layout = tieredOrbitalLayout(nodes, anchorId);
    // Selection-driven dimming should ONLY kick in for non-anchor selections.
    // The anchor is auto-selected on page load (so the right-rail detail panel
    // shows Jeffrey Epstein). If that auto-select dimmed every other card the
    // public board would look 32% washed out by default — which conflicts with
    // the case-board reference visual where all peripheral cards are at full
    // brightness. So we treat anchor-as-selection as "no selection" for the
    // purpose of dim/highlight logic, while still letting the right rail
    // render JE's detail.
    const isAnchorOnlySelection = selectedEntityId === anchorId;
    const hasSelection = !!selectedEntityId && !isAnchorOnlySelection;
    const fnodes = nodes.map((e) => {
      const isAnchor = e.id === anchorId;
      const isSelected = e.id === selectedEntityId && !isAnchor;
      // Dim every non-anchor, non-selected card while a selection is active.
      // The anchor stays full strength so the board still reads as anchored.
      const isDimmed = hasSelection && !isAnchor && !isSelected;
      return {
        id: e.id,
        type: "evidence",
        position: layout[e.id] || { x: 0, y: 0 },
        // zIndex order: dimmed < normal < anchor < selected. ReactFlow respects
        // node.zIndex for stacking. The selected card MUST render above edges
        // and labels so its text can never be occluded.
        zIndex: isSelected ? 1000 : isAnchor ? 50 : isDimmed ? 5 : 10,
        data: {
          entity: e,
          isAnchor,
          isSelected,
          isDimmed,
          tier: classifyTier(e, anchorId),
          onSelect: onSelectEntity,
          anchorImage,
        },
        draggable: false,
      };
    });
    const fedges = edges.map((r) => {
      const meta = REL_META[r.type] || REL_META.other_editor_defined;
      const conf = CONFIDENCE_META[r.confidence] || CONFIDENCE_META.medium;
      const isRelSelected = selectedRelId === r.id;
      const touchesSelectedEntity = hasSelection
        && (r.src_entity_id === selectedEntityId || r.dst_entity_id === selectedEntityId);
      const label = compactEdgeLabel(r);
      const isLow = r.confidence === "low" || ((r.source_refs || []).length === 0);
      const stroke = isLow ? "#7A8294" : "#D92D20";

      // Edge opacity policy:
      //   - no selection at all      → normal
      //   - entity selected, edge touches it → emphasised
      //   - entity selected, edge does not touch → strongly dimmed
      //   - edge selected            → handled by isRelSelected branch
      let edgeOpacity;
      if (isRelSelected) {
        edgeOpacity = isLow ? 0.85 : 1.0;
      } else if (hasSelection) {
        edgeOpacity = touchesSelectedEntity ? (isLow ? 0.75 : 0.95) : 0.10;
      } else if (selectedRelId) {
        edgeOpacity = 0.28;  // some OTHER edge is selected
      } else {
        edgeOpacity = isLow ? 0.65 : 0.85;
      }

      const showLabel = label && (
        !hasSelection ? true : (touchesSelectedEntity || isRelSelected)
      );

      return {
        id: r.id,
        source: r.src_entity_id,
        target: r.dst_entity_id,
        type: "straight",
        animated: false,
        // Keep edges underneath nodes; ReactFlow paints edges first by default
        // but we explicitly mark non-relevant edges as not interactive when
        // a node is selected so the cursor goes through.
        style: {
          stroke,
          strokeWidth: isRelSelected ? 3.2 : touchesSelectedEntity ? 2.4 : 2.0,
          strokeDasharray: isLow ? "5 4" : undefined,
          strokeLinecap: "round",
          // Inline filter is the baseline drop-shadow. We OMIT it on edges that
          // pulse (touchesSelectedEntity && !isRelSelected) so the CSS keyframe
          // animation on the same property can take over without conflict.
          filter: isRelSelected
            ? "drop-shadow(0 0 4px rgba(217,45,32,0.55))"
            : (touchesSelectedEntity ? undefined : "drop-shadow(0 1px 1px rgba(0,0,0,0.6))"),
          opacity: edgeOpacity,
          pointerEvents: hasSelection && !touchesSelectedEntity ? "none" : "auto",
        },
        label: showLabel ? label : undefined,
        labelStyle: {
          fill: "#F2EEE3",
          fontSize: 10,
          fontFamily: "'IBM Plex Mono', monospace",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontWeight: 600,
          opacity: hasSelection && !touchesSelectedEntity && !isRelSelected ? 0 : 1,
          // SVG paint-order trick: stroke the text first with a dark colour,
          // then paint the fill on top. Gives a 1.5px outline that reads
          // through any line/card behind the label. Lightweight, no new
          // elements.
          paintOrder: "stroke",
          stroke: "#0a0907",
          strokeWidth: 3,
          strokeLinejoin: "round",
        },
        labelBgStyle: {
          fill: "#0F0F0F",
          fillOpacity: 1.0,
          stroke: isLow ? "#7A8294" : "#D92D20",
          strokeOpacity: 0.8,
          strokeWidth: 0.75,
        },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 0,
        // Connection-focus mode: only edges touching the active card pulse.
        // The pulse is a soft drop-shadow oscillation, NOT an opacity or
        // stroke-width change, so the existing dim/highlight logic stays in
        // charge of visibility. Edges colored slate (low confidence) get a
        // slate pulse, red edges get a red pulse, matching their stroke.
        className: touchesSelectedEntity && !isRelSelected
          ? (isLow ? "ef-edge-pulse-slate" : "ef-edge-pulse-red")
          : undefined,
        data: { rel: r, conf, meta },
      };
    });
    return { flowNodes: fnodes, flowEdges: fedges };
  }, [nodes, edges, anchorId, onSelectEntity, anchorImage, selectedRelId, selectedEntityId]);

  const onEdgeMouseEnter = useCallback((evt, e) => {
    const containerRect = evt.currentTarget.closest("[data-ef-board]")?.getBoundingClientRect();
    if (!containerRect) return;
    setHover({
      rel: e.data?.rel,
      x: evt.clientX - containerRect.left,
      y: evt.clientY - containerRect.top,
    });
  }, []);
  const onEdgeMouseMove = useCallback((evt) => {
    const containerRect = evt.currentTarget.closest("[data-ef-board]")?.getBoundingClientRect();
    if (!containerRect || !hover) return;
    setHover((h) => h ? { ...h, x: evt.clientX - containerRect.left, y: evt.clientY - containerRect.top } : null);
  }, [hover]);
  const onEdgeMouseLeave = useCallback(() => setHover(null), []);

  const onEdgeClick = useCallback((_, e) => {
    setSelectedRelId(e.id);
    onSelectEdge?.(e.data?.rel);
  }, [onSelectEdge]);

  // Mobile-only camera tuning. Tracked via matchMedia so we react to
  // orientation changes / window resizes without forcing a remount.
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)").matches : false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia("(max-width: 640px)");
    const onChange = (e) => setIsMobile(e.matches);
    try { m.addEventListener("change", onChange); }
    catch { m.addListener(onChange); }
    return () => {
      try { m.removeEventListener("change", onChange); }
      catch { m.removeListener(onChange); }
    };
  }, []);

  // Mobile gets generous fit padding so the whole investigation board is
  // visible on first paint and never feels pressed against the viewport.
  // Desktop padding is unchanged.
  const fitPadding = isMobile ? 0.4 : 0.1;
  const minZoom = isMobile ? 0.12 : 0.25;

  // Capture the ReactFlow instance so the Overview button can re-fit on demand.
  const rfRef = React.useRef(null);
  const handleInit = useCallback((rf) => { rfRef.current = rf; }, []);
  const resetOverview = useCallback(() => {
    const rf = rfRef.current;
    if (!rf) return;
    try { rf.fitView({ padding: fitPadding, duration: 450 }); }
    catch { /* noop */ }
  }, [fitPadding]);

  return (
    <div
      className="relative border border-[#2D2A26] bg-black overflow-hidden"
      style={{ height,
               backgroundImage:
                 "radial-gradient(ellipse at 50% 50%, rgba(40,30,22,0.35) 0%, transparent 70%), radial-gradient(ellipse at 50% 0%, rgba(217,45,32,0.06) 0%, transparent 55%)" }}
      data-testid="ef-evidence-board"
      data-ef-board=""
    >
      {/* Connection-focus pulse — subtle drop-shadow oscillation, ONLY on edges
          touching the active card. Disabled for users who prefer reduced motion. */}
      <style>{`
        @keyframes ef-pulse-red {
          0%, 100% { filter: drop-shadow(0 0 0.5px rgba(217,45,32,0.25)); }
          50%      { filter: drop-shadow(0 0 3px   rgba(217,45,32,0.65)); }
        }
        @keyframes ef-pulse-slate {
          0%, 100% { filter: drop-shadow(0 0 0.5px rgba(122,130,148,0.25)); }
          50%      { filter: drop-shadow(0 0 2.5px rgba(122,130,148,0.55)); }
        }
        .ef-edge-pulse-red .react-flow__edge-path {
          animation: ef-pulse-red 2.6s ease-in-out infinite;
          will-change: filter;
        }
        .ef-edge-pulse-slate .react-flow__edge-path {
          animation: ef-pulse-slate 3.0s ease-in-out infinite;
          will-change: filter;
        }
        @media (prefers-reduced-motion: reduce) {
          .ef-edge-pulse-red .react-flow__edge-path,
          .ef-edge-pulse-slate .react-flow__edge-path { animation: none; }
        }
      `}</style>
      {/* Cork-grain noise layer — kept extremely subtle for premium dossier feel
          without competing with the screenshot's pure-black aesthetic. */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
           style={{ backgroundImage:
             "radial-gradient(circle at 20% 30%, rgba(140,98,58,0.5) 0px, transparent 2px), radial-gradient(circle at 75% 65%, rgba(100,72,42,0.4) 0px, transparent 2.5px), radial-gradient(circle at 45% 80%, rgba(120,88,52,0.45) 0px, transparent 1.8px)",
             backgroundSize: "8px 8px, 11px 11px, 7px 7px" }} />
      {/* Subtle red-room atmosphere */}
      <div className="absolute inset-0 pointer-events-none opacity-25 mix-blend-screen"
           style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(217,45,32,0.14) 0%, transparent 55%)" }} />

      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        onEdgeClick={onEdgeClick}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseMove={onEdgeMouseMove}
        onEdgeMouseLeave={onEdgeMouseLeave}
        onPaneClick={() => {
          setSelectedRelId(null);
          // Background click clears the active entity selection too — this is
          // the readability-fix contract: tap empty board to bring everything
          // back to normal opacity.
          onClearSelection?.();
        }}
        fitView
        onInit={handleInit}
        fitViewOptions={{ padding: fitPadding }}
        minZoom={minZoom}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={24} size={0.6} color="#222019" />
        <Controls
          className="!bg-[#0F0F0F] !border-[#2D2A26]"
          showInteractive={false}
        />
      </ReactFlow>

      <EdgeTooltip rel={hover?.rel} x={hover?.x || 0} y={hover?.y || 0} nodeMap={nodeMap} />

      {/* Overview reset button — always returns the camera to the full-board
          view. Pinned bottom-right above the legend bar so it never collides
          with the ReactFlow Controls (which sit bottom-left). */}
      <button
        onClick={resetOverview}
        data-testid="ef-board-overview"
        title="Reset to full-board overview"
        className="absolute right-2 bottom-12 z-30 inline-flex items-center gap-1.5 bg-[#0F0F0F]/90 hover:bg-[#D92D20] hover:text-white border border-[#2D2A26] hover:border-[#D92D20] text-[#A39E93] font-mono text-[9px] uppercase tracking-[0.25em] px-2.5 py-1.5 backdrop-blur-sm transition-colors"
      >
        ⊕ OVERVIEW
      </button>

      {/* Bottom legend bar — matches the reference screenshot. Solid red line =
          confirmed / source-backed; dashed slate line = supporting / low
          confidence. Pinned to the bottom of the board surface, never overlaps
          a node because of the bottom padding in `fitViewOptions`. */}
      <div
        className="absolute left-0 right-0 bottom-0 border-t border-[#2D2A26] bg-[#0a0907]/95 backdrop-blur-sm px-3 py-2 flex flex-wrap items-center gap-3 sm:gap-5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93] z-30 pointer-events-none"
        data-testid="ef-board-legend"
      >
        <span className="text-[#D92D20]">// LEGEND</span>
        <span className="flex items-center gap-2">
          <span className="inline-block w-7 h-[2px] bg-[#D92D20]" />
          CONFIRMED / SOURCE-BACKED
        </span>
        <span className="flex items-center gap-2">
          <span
            className="inline-block w-7 h-[2px]"
            style={{ background:
              "repeating-linear-gradient(90deg, #7A8294 0 4px, transparent 4px 8px)" }}
          />
          SUPPORTING / LOW CONFIDENCE
        </span>
      </div>
    </div>
  );
};

export default EvidenceBoard;
